import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { Button, buttonVariants } from "@/common/components/ui/button";
import { Calendar } from "@/common/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/common/components/ui/popover";
import { cn } from "@/common/utils/cn";
import { VariantProps } from "class-variance-authority";

interface DatePickerProps extends VariantProps<typeof buttonVariants> {
  className?: string;
  inputClassName?: string;
  name: string;
  label?: string;
  date: Date | undefined;
  setDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
  markAsRequired?: boolean;
  errorMessage?: string;
  formatter?: (date: Date) => string;
  numberOfMonths?: number;
}

export function DatePicker({
  className,
  inputClassName,
  variant,
  label,
  date,
  setDate,
  markAsRequired,
  errorMessage,
  formatter,
  numberOfMonths,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className={cn("flex flex-col", className)}>
      {label ? (
        <label htmlFor="date" className="text-xs self-start mb-1 cursor-pointer">
          {label}
          {markAsRequired && <span className="text-danger-soft"> *</span>}
        </label>
      ) : null}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={variant ?? "badge"}
            size={"badge"}
            className={cn(
              "justify-start text-left font-normal bg-white-100 rounded-xl",
              inputClassName,
              !date && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />

            {date && formatter ? (
              formatter(date)
            ) : date ? (
              format(date, 'dd/MM/yy')
            ) : (
              <span>Selecione uma data</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-[#212023] border-neutral-op-8 dark" align="start">
          <Calendar
            initialFocus
            mode="single"
            defaultMonth={date}
            selected={date}
            onSelect={(value) => {
              setDate(value);
              setIsOpen(false);
            }}
            numberOfMonths={numberOfMonths || 2}
          />
        </PopoverContent>
      </Popover>

      {errorMessage && <span className="text-danger-soft text-sm">{errorMessage}</span>}
    </div>
  );
}
